import { useEffect, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Plus, Search, MapPin, List, Map as MapIcon, Building2, Trash2, Edit, X, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { supplierAPI } from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import useAuthStore from '@/store/authStore';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons by vendor type
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
};

const vendorTypeColors = {
  dealer: '#3b82f6',    // blue
  parts: '#22c55e',     // green
  service: '#f59e0b',   // amber
  supplier: '#8b5cf6',  // purple
};

const companyGroupColors = {
  'CIT Trucks': '#ef4444',
  'Trans Chicago': '#3b82f6',
  'JX Truck Center': '#22c55e',
  'Rush Truck Centers': '#f59e0b',
  'M&K Truck Centers': '#8b5cf6',
  'Parts & Services': '#6b7280',
};

// Map bounds fitter component
const FitBounds = ({ vendors }) => {
  const map = useMap();
  
  useEffect(() => {
    if (vendors.length > 0) {
      const bounds = L.latLngBounds(
        vendors
          .filter(v => v.latitude && v.longitude)
          .map(v => [v.latitude, v.longitude])
      );
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [vendors, map]);
  
  return null;
};

const VendorForm = ({ vendor, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: vendor?.name || '',
    company_group: vendor?.company_group || '',
    address: vendor?.address || '',
    city: vendor?.city || '',
    state: vendor?.state || '',
    country: vendor?.country || 'USA',
    latitude: vendor?.latitude || '',
    longitude: vendor?.longitude || '',
    phone: vendor?.phone || '',
    email: vendor?.email || '',
    vendor_type: vendor?.vendor_type || 'supplier',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.address) {
      toast.error('Name and address are required');
      return;
    }
    onSave({
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 space-y-2">
          <Label>Vendor Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Vendor name"
            required
            data-testid="vendor-name-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Company Group</Label>
          <Input
            value={formData.company_group}
            onChange={(e) => setFormData(prev => ({ ...prev, company_group: e.target.value }))}
            placeholder="e.g., CIT Trucks"
            data-testid="vendor-group-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Vendor Type</Label>
          <Select value={formData.vendor_type} onValueChange={(v) => setFormData(prev => ({ ...prev, vendor_type: v }))}>
            <SelectTrigger data-testid="vendor-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dealer">Dealer</SelectItem>
              <SelectItem value="parts">Parts</SelectItem>
              <SelectItem value="service">Service</SelectItem>
              <SelectItem value="supplier">Supplier</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Address *</Label>
        <Input
          value={formData.address}
          onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
          placeholder="Street address"
          required
          data-testid="vendor-address-input"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>City</Label>
          <Input
            value={formData.city}
            onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
            placeholder="City"
          />
        </div>
        <div className="space-y-2">
          <Label>State</Label>
          <Input
            value={formData.state}
            onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
            placeholder="IL"
          />
        </div>
        <div className="space-y-2">
          <Label>Country</Label>
          <Input
            value={formData.country}
            onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
            placeholder="USA"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Latitude</Label>
          <Input
            type="number"
            step="0.0001"
            value={formData.latitude}
            onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
            placeholder="41.8781"
          />
        </div>
        <div className="space-y-2">
          <Label>Longitude</Label>
          <Input
            type="number"
            step="0.0001"
            value={formData.longitude}
            onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
            placeholder="-87.6298"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="+1 555-0100"
          />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="contact@vendor.com"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading} data-testid="save-vendor-btn">
          {loading ? 'Saving...' : vendor ? 'Update Vendor' : 'Add Vendor'}
        </Button>
      </div>
    </form>
  );
};

export default function VendorsPage() {
  const { user } = useAuthStore();
  const [vendors, setVendors] = useState([]);
  const [companyGroups, setCompanyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('map'); // 'list' or 'map'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const canEdit = ['system_admin', 'purchasing'].includes(user?.role);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const params = {};
      if (groupFilter !== 'all') params.company_group = groupFilter;
      if (typeFilter !== 'all') params.vendor_type = typeFilter;
      
      const response = await supplierAPI.getAll(searchQuery || undefined, params.company_group, params.vendor_type);
      setVendors(response.data.suppliers || []);
      setCompanyGroups(response.data.company_groups || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [groupFilter, typeFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchVendors();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const filteredVendors = useMemo(() => {
    return vendors.filter(v => v.latitude && v.longitude);
  }, [vendors]);

  const handleSaveVendor = async (data) => {
    setSaving(true);
    try {
      if (editingVendor) {
        await supplierAPI.update(editingVendor.id, data);
        toast.success('Vendor updated');
      } else {
        await supplierAPI.create(data);
        toast.success('Vendor added');
      }
      setDialogOpen(false);
      setEditingVendor(null);
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVendor = async (vendor) => {
    if (!confirm(`Delete ${vendor.name}?`)) return;
    try {
      await supplierAPI.delete(vendor.id);
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setDialogOpen(true);
  };

  const handleAddVendor = () => {
    setEditingVendor(null);
    setDialogOpen(true);
  };

  // Group vendors by company for stats
  const vendorStats = useMemo(() => {
    const stats = {};
    vendors.forEach(v => {
      const group = v.company_group || 'Other';
      stats[group] = (stats[group] || 0) + 1;
    });
    return stats;
  }, [vendors]);

  return (
    <div className="space-y-6" data-testid="vendors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Vendors</h1>
          <p className="text-slate-600">{vendors.length} locations</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'map' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none"
              onClick={() => setViewMode('map')}
              data-testid="view-map-btn"
            >
              <MapIcon className="w-4 h-4 mr-1" />
              Map
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none"
              onClick={() => setViewMode('list')}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4 mr-1" />
              List
            </Button>
          </div>
          
          {canEdit && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAddVendor} data-testid="add-vendor-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Vendor
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
                  <DialogDescription>
                    {editingVendor ? 'Update vendor information' : 'Add a new vendor location'}
                  </DialogDescription>
                </DialogHeader>
                <VendorForm
                  vendor={editingVendor}
                  onSave={handleSaveVendor}
                  onCancel={() => { setDialogOpen(false); setEditingVendor(null); }}
                  loading={saving}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Stats by company group */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(vendorStats).map(([group, count]) => (
          <Badge
            key={group}
            variant="outline"
            className="cursor-pointer hover:bg-slate-100"
            style={{ borderColor: companyGroupColors[group] || '#6b7280' }}
            onClick={() => setGroupFilter(group === groupFilter ? 'all' : group)}
          >
            <span 
              className="w-2 h-2 rounded-full mr-2"
              style={{ backgroundColor: companyGroupColors[group] || '#6b7280' }}
            />
            {group}: {count}
          </Badge>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search vendors..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-vendors"
              />
            </div>
            <Select value={groupFilter} onValueChange={setGroupFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="group-filter">
                <SelectValue placeholder="All Groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Groups</SelectItem>
                {companyGroups.map(group => (
                  <SelectItem key={group} value={group}>{group}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-36" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="dealer">Dealer</SelectItem>
                <SelectItem value="parts">Parts</SelectItem>
                <SelectItem value="service">Service</SelectItem>
                <SelectItem value="supplier">Supplier</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-[500px] w-full" />
          </CardContent>
        </Card>
      ) : viewMode === 'map' ? (
        /* Map View */
        <Card>
          <CardContent className="p-0 overflow-hidden rounded-lg">
            <div className="h-[600px] relative">
              <MapContainer
                center={[41.8781, -87.6298]}
                zoom={9}
                className="h-full w-full"
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds vendors={filteredVendors} />
                {filteredVendors.map((vendor) => (
                  <Marker
                    key={vendor.id}
                    position={[vendor.latitude, vendor.longitude]}
                    icon={createCustomIcon(companyGroupColors[vendor.company_group] || vendorTypeColors[vendor.vendor_type] || '#6b7280')}
                    eventHandlers={{
                      click: () => setSelectedVendor(vendor),
                    }}
                  >
                    <Popup>
                      <div className="min-w-[200px]">
                        <h3 className="font-semibold text-slate-900">{vendor.name}</h3>
                        {vendor.company_group && (
                          <Badge variant="outline" className="text-xs mt-1 mb-2">
                            {vendor.company_group}
                          </Badge>
                        )}
                        <p className="text-sm text-slate-600 flex items-start gap-1 mt-2">
                          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                          {vendor.address}, {vendor.city}, {vendor.state}
                        </p>
                        {vendor.phone && (
                          <p className="text-sm text-slate-600 mt-1">{vendor.phone}</p>
                        )}
                        {canEdit && (
                          <div className="flex gap-2 mt-3 pt-2 border-t">
                            <Button size="sm" variant="outline" onClick={() => handleEditVendor(vendor)}>
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
              
              {/* Legend */}
              <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg z-[1000]">
                <p className="text-xs font-semibold text-slate-700 mb-2">Company Groups</p>
                <div className="space-y-1">
                  {Object.entries(companyGroupColors).map(([group, color]) => (
                    <div key={group} className="flex items-center gap-2 text-xs">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-slate-600">{group}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* List View */
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider">Vendor</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider">Company Group</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider">Address</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider">Type</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider">Contact</TableHead>
                    {canEdit && <TableHead className="text-xs font-bold uppercase tracking-wider w-24">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canEdit ? 6 : 5} className="text-center py-12 text-slate-500">
                        No vendors found
                      </TableCell>
                    </TableRow>
                  ) : (
                    vendors.map((vendor) => (
                      <TableRow key={vendor.id} className="hover:bg-slate-50" data-testid={`vendor-row-${vendor.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: companyGroupColors[vendor.company_group] || '#6b7280' }}
                            />
                            <span className="font-medium text-slate-900">{vendor.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {vendor.company_group || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          <div className="flex items-start gap-1">
                            <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                            <span>{vendor.address}, {vendor.city}, {vendor.state}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs capitalize">
                            {vendor.vendor_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {vendor.phone || vendor.email || '-'}
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="ghost" onClick={() => handleEditVendor(vendor)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteVendor(vendor)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
